import {
  ApiError,
  type ChargeableCard,
  type CustomerPaymentMethod,
  queryKeys,
  type RentalCardIntent,
} from '@mevaser/shared'
import { Badge, Button, EmptyState, Skeleton, useToast } from '@mevaser/ui'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import type { Reader, Terminal } from '@stripe/terminal-js'
import { loadStripeTerminal } from '@stripe/terminal-js'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CreditCard } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api.js'
import { getStripe } from '../lib/stripe.js'

/**
 * Integrated card charging for rentals (t0941), factored out of
 * `RecordPaymentDialog` so a damage fee (t0944) can reuse the exact same
 * saved-card, keyed-in-card, and reader flows instead of a second copy. Only
 * `SavedCardPanel` needs its own `note` — a card intent's note is set once,
 * when the parent dialog opens the PaymentIntent, so `NewCardPanel` and
 * `ReaderPanel` never need to know about it.
 */
export type ChargeableRentalKind = 'fee' | 'deposit' | 'damage_fee'

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function isExpired(card: CustomerPaymentMethod): boolean {
  const now = new Date()
  const thisMonth = now.getFullYear() * 12 + now.getMonth()
  const cardMonth = card.expYear * 12 + (card.expMonth - 1)
  return cardMonth < thisMonth
}

export function SavedCardPanel({
  rentalId,
  customerId,
  kind,
  amountCents,
  note = null,
  validAmount,
  onSettled,
}: {
  rentalId: string
  customerId: string
  kind: ChargeableRentalKind
  amountCents: number
  /** The damage reason — required by the server whenever `kind` is
   *  `damage_fee`, ignored otherwise. */
  note?: string | null
  validAmount: boolean
  onSettled: () => void
}) {
  const [selected, setSelected] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data: cards, isLoading } = useQuery({
    queryKey: queryKeys.customers.chargeableCards(customerId),
    // Includes cards of customers linked to this one (t1117), each marked with
    // its owner — the card stays on the owner's file either way.
    queryFn: () =>
      api.get<ChargeableCard[]>(`/customers/${customerId}/payment-methods`, {
        query: { includeLinked: true },
      }),
  })

  const charge = useMutation({
    mutationFn: () =>
      api.post(`/rentals/${rentalId}/payments/charge-saved-card`, {
        kind,
        amountCents,
        paymentMethodId: selected,
        note,
      }),
    onSuccess: onSettled,
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Could not charge this card'),
  })

  if (isLoading) return <Skeleton className="h-16 w-full" />

  if (!cards?.length) {
    return (
      <EmptyState
        icon={CreditCard}
        title="No saved cards"
        description="Add one from the customer's page, or link a family member who has one, then come back here."
      />
    )
  }

  return (
    <div className="space-y-3">
      <ul className="divide-y divide-border rounded-lg border border-border">
        {cards.map((card) => {
          const expired = isExpired(card)
          return (
            <li key={card.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <input
                type="radio"
                name="rental-saved-card"
                checked={selected === card.id}
                disabled={expired}
                onChange={() => setSelected(card.id)}
                className="size-4 accent-primary"
              />
              <div className="min-w-0 flex-1">
                <p className="font-identifier">
                  {capitalize(card.brand)} •••• {card.last4}
                </p>
                <p className="text-2xs text-foreground-muted">
                  Expires {String(card.expMonth).padStart(2, '0')}/{card.expYear}
                </p>
              </div>
              {card.owner ? <Badge tone="neutral">Card of {card.owner.name}</Badge> : null}
              {expired ? <Badge tone="danger">Expired</Badge> : null}
            </li>
          )
        })}
      </ul>
      {error ? (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      ) : null}
      <Button
        variant="primary"
        className="w-full"
        disabled={!selected || !validAmount || charge.isPending}
        loading={charge.isPending}
        onClick={() => {
          setError(null)
          charge.mutate()
        }}
      >
        {error ? 'Try again' : kind === 'deposit' ? 'Place hold' : 'Charge card'}
      </Button>
    </div>
  )
}

/** Mounts Stripe.js against the card-intent's client secret — same split as
 *  `CardSetupPanel`/`CardSetupForm` in `saved-cards-card.tsx`, `useStripe`/
 *  `useElements` only work inside `<Elements>`. */
export function NewCardPanel({
  intent,
  rentalId,
  kind,
  onSettled,
}: {
  intent: RentalCardIntent
  rentalId: string
  kind: ChargeableRentalKind
  onSettled: () => void
}) {
  const stripePromise = useMemo(() => getStripe(intent.publishableKey), [intent.publishableKey])

  return (
    <Elements stripe={stripePromise} options={{ clientSecret: intent.clientSecret }}>
      <NewCardForm intent={intent} rentalId={rentalId} kind={kind} onSettled={onSettled} />
    </Elements>
  )
}

function NewCardForm({
  intent,
  rentalId,
  kind,
  onSettled,
}: {
  intent: RentalCardIntent
  rentalId: string
  kind: ChargeableRentalKind
  onSettled: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState<string | null>(null)
  const [isWorking, setIsWorking] = useState(false)

  const confirm = useMutation({
    mutationFn: () =>
      api.post(`/rentals/${rentalId}/payments/${intent.rentalPaymentId}/confirm`, {}),
    onSuccess: onSettled,
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Could not confirm the charge'),
  })

  const handleCharge = async () => {
    if (!stripe || !elements) return
    setError(null)
    setIsWorking(true)
    try {
      const result = await stripe.confirmPayment({ elements, redirect: 'if_required' })
      if (result.error) {
        setError(result.error.message ?? 'The card was not accepted. Please try again.')
        return
      }
      await confirm.mutateAsync()
    } catch {
      // confirm's onError already recorded the message in `error` state.
    } finally {
      setIsWorking(false)
    }
  }

  return (
    <div className="space-y-3">
      <PaymentElement />
      {error ? (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      ) : null}
      <Button
        variant="primary"
        className="w-full"
        disabled={!stripe || !elements || isWorking}
        loading={isWorking}
        onClick={() => void handleCharge()}
      >
        {error ? 'Try again' : kind === 'deposit' ? 'Place hold' : 'Charge card'}
      </Button>
    </div>
  )
}

/**
 * A connected physical reader. Discovery/connection happens as soon as this
 * method is picked, independent of the amount — the reader itself has
 * nothing to do with which PaymentIntent it eventually collects for.
 * `onReady` tells the parent when "Continue" (which opens the PaymentIntent)
 * should become available; once `intent` arrives, this collects and
 * processes the charge against it and confirms the result on the server.
 */
export function ReaderPanel({
  rentalId,
  intent,
  kind,
  onReady,
  onSettled,
}: {
  rentalId: string
  intent: RentalCardIntent | null
  kind: ChargeableRentalKind
  onReady: (ready: boolean) => void
  onSettled: () => void
}) {
  const [terminal, setTerminal] = useState<Terminal | null>(null)
  const [readers, setReaders] = useState<Reader[]>([])
  const [connected, setConnected] = useState<Reader | null>(null)
  const [status, setStatus] = useState('Looking for a reader…')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function setup() {
      const StripeTerminal = await loadStripeTerminal()
      if (!StripeTerminal || cancelled) return
      const t = StripeTerminal.create({
        onFetchConnectionToken: async () => {
          const { secret } = await api.post<{ secret: string }>(
            '/rentals/terminal/connection-token',
            {},
          )
          return secret
        },
        onUnexpectedReaderDisconnect: () => {
          if (!cancelled) {
            setConnected(null)
            onReady(false)
            setStatus('The reader disconnected — reconnect to try again.')
          }
        },
      })
      if (cancelled) return
      setTerminal(t)

      const result = await t.discoverReaders({ simulated: import.meta.env.DEV })
      if (cancelled) return
      if ('error' in result) {
        setError(result.error.message)
        return
      }
      setReaders(result.discoveredReaders)
      setStatus(
        result.discoveredReaders.length
          ? 'Choose a reader to connect.'
          : 'No readers found nearby.',
      )
    }
    void setup()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per mount, when "Card reader" is picked
  }, [])

  const connect = async (reader: Reader) => {
    if (!terminal) return
    setError(null)
    setStatus(`Connecting to ${reader.label ?? reader.id}…`)
    const result = await terminal.connectReader(reader)
    if ('error' in result) {
      setError(result.error.message)
      return
    }
    setConnected(result.reader)
    setStatus(
      kind === 'deposit'
        ? 'Reader connected — press Continue to place the hold.'
        : 'Reader connected — press Continue to charge.',
    )
    onReady(true)
  }

  // Once the parent has opened a PaymentIntent for this reader, collect and
  // process the charge, then settle it on the server the same way the
  // card-entry flow does.
  useEffect(() => {
    if (!intent || !terminal || !connected) return
    let cancelled = false
    async function collect() {
      setStatus('Insert, tap, or swipe the card…')
      const collected = await terminal!.collectPaymentMethod(intent!.clientSecret)
      if (cancelled) return
      if ('error' in collected) {
        setError(collected.error.message)
        return
      }
      setStatus('Processing…')
      const processed = await terminal!.processPayment(collected.paymentIntent)
      if (cancelled) return
      if ('error' in processed) {
        setError(processed.error.message)
        return
      }
      await api.post(`/rentals/${rentalId}/payments/${intent!.rentalPaymentId}/confirm`, {})
      if (!cancelled) onSettled()
    }
    void collect()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires once per intent
  }, [intent])

  return (
    <div className="space-y-3">
      <p className="text-sm text-foreground-muted">{status}</p>
      {error ? (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      ) : null}
      {!connected && readers.length ? (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {readers.map((reader) => (
            <li key={reader.id} className="flex items-center justify-between px-3 py-2 text-sm">
              <span>{reader.label ?? reader.id}</span>
              <Button size="sm" variant="secondary" onClick={() => void connect(reader)}>
                Connect
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
